import React, { useEffect, useRef, useState } from 'react';
import { ButtonRounded, TextInput } from 'is24-corecss';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiResponseError } from '../../api/ApiResponseError';
import { startWebIdVerification } from '../../api/webid/webIdVerification';
import { WebIdVerificationAttempt } from '../../api/webid/model/WebIdVerificationAttempt';
import { trackEvent } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';
import {
  normalizeBirthdate,
  validateBirthdate,
  validateFirstName,
  validateLastName,
  validatePhoneNumber,
} from '../../pages/verificationLandingPage/validators/Validators';

interface WebIdVerificationProps {
  onContentChange: (content: React.ReactNode) => void;
  onError: (error: string) => void;
  setPhoneNumberForTan: (phoneNumber: string | null) => void;
}

const WebIdVerification: React.FC<WebIdVerificationProps> = ({ onContentChange, onError, setPhoneNumberForTan }) => {
  const { t } = useTranslation();
  const {
    clientId,
    userFirstName,
    userLastName,
    userBirthdate,
    userEmail,
    userPhoneNumber,
    nameEditable,
    birthdateEditable,
    phoneNumberEditable,
    userId,
  } = useClientConfig();
  const [, setSearchParams] = useSearchParams();
  const [isRequestPending, setIsRequestPending] = useState(false);
  // State is updated asynchronously. Keep an immediate guard as well, otherwise two
  // quick clicks can create two WebID sessions and two different TANs for one phone.
  const isProfileCheckInFlight = useRef(false);
  const isNameFieldsDisabled = nameEditable === false;
  const isBirthdateDisabled = birthdateEditable === false;
  const isPhoneNumberDisabled = phoneNumberEditable === false;
  const [firstName, setFirstName] = useState(userFirstName || '');
  const [lastName, setLastName] = useState(userLastName || '');
  const [birthdate, setBirthdate] = useState(userBirthdate || '');
  const [phoneNumber, setPhoneNumber] = useState(userPhoneNumber || '');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [birthdateError, setBirthdateError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');

  useEffect(() => {
    onContentChange(
      <div className='grid grid-flex gutter-horizontal-m margin-top-m'>
        <div className='grid-item one-half palm-one-whole palm-margin-bottom-l'>
          <TextInput
            data-testid='first-name-input'
            id='first-name'
            label={t('VerificationLandingPage.inputLabelFName')}
            mandatory={true}
            value={firstName}
            disabled={isNameFieldsDisabled}
            onChange={(event) => setFirstName(event.target.value)}
            valid={firstNameError.length === 0}
            captionText={firstNameError}
            placeholder='Max'
          />
        </div>
        <div className='grid-item one-half palm-one-whole margin-bottom-l'>
          <TextInput
            data-testid='last-name-input'
            id='last-name'
            label={t('VerificationLandingPage.inputLabelLName')}
            mandatory={true}
            value={lastName}
            disabled={isNameFieldsDisabled}
            onChange={(event) => setLastName(event.target.value)}
            valid={lastNameError.length === 0}
            captionText={lastNameError}
            placeholder='Mustermann'
          />
        </div>
        <div className='grid-item one-half palm-one-whole margin-bottom-l'>
          <TextInput
            data-testid='birthdate-input'
            id='with-date-state'
            label={t('VerificationLandingPage.inputLabelBirthdate' as any)}
            placeholder='DD.MM.YYYY'
            type='date'
            mandatory={true}
            value={birthdate}
            disabled={isBirthdateDisabled}
            onChange={(event) => setBirthdate(event.target.value)}
            valid={birthdateError.length === 0}
            captionText={birthdateError}
          />
        </div>
        <div className='grid-item one-half palm-one-whole'>
          <TextInput
            data-testid='phone-number-input'
            id='for telephone numbers'
            type='tel'
            label={t('VerificationLandingPage.inputLabelPhoneNumber' as any)}
            placeholder='+49 170 1234567'
            mandatory={true}
            value={phoneNumber}
            disabled={isPhoneNumberDisabled}
            onChange={(event) => setPhoneNumber(event.target.value)}
            valid={phoneNumberError.length === 0}
            captionText={phoneNumberError}
          />
        </div>
        <div className='grid grid-flex font-body-small-regular font-lightgray padding-left-m margin-top-xxl palm-margin-top-xl palm-margin-bottom-l'>
          {t('VerificationLandingPage.webIdHelperText' as any)}
        </div>
      </div>,
    );
  }, [
    birthdate,
    birthdateError,
    firstName,
    firstNameError,
    isBirthdateDisabled,
    isNameFieldsDisabled,
    isPhoneNumberDisabled,
    lastName,
    lastNameError,
    onContentChange,
    phoneNumber,
    phoneNumberError,
    t,
  ]);

  const validateUserInput = () => {
    const isFirstNameValid = validateFirstName(firstName);
    const isLastNameValid = validateLastName(lastName);
    const isBirthdateValid = validateBirthdate(birthdate);
    const isPhoneNumberValid = validatePhoneNumber(phoneNumber);

    setFirstNameError(isFirstNameValid ? '' : t('VerificationLandingPage.firstNameError'));
    setLastNameError(isLastNameValid ? '' : t('VerificationLandingPage.lastNameError'));
    setBirthdateError(isBirthdateValid ? '' : t('VerificationLandingPage.birthdateError' as any));
    setPhoneNumberError(isPhoneNumberValid ? '' : t('VerificationLandingPage.phoneNumberError' as any));

    return isFirstNameValid && isLastNameValid && isBirthdateValid && isPhoneNumberValid;
  };

  const navigateToWebIdFlow = (state: 'verification_webid_tan' | 'webid_verification_start', sessionId: string) => {
    setSearchParams(
      (prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set('tvs_session_id', sessionId);
        newParams.set('tvs_state', state);
        return newParams;
      },
      { replace: true },
    );
  };

  const onWebIdVerificationClick = async () => {
    if (isProfileCheckInFlight.current) {
      return;
    }
    if (!validateUserInput()) {
      return;
    }
    if (userId && !userEmail?.trim()) {
      onError('errors.webid.bad.request' as any);
      return;
    }
    const webIdVerificationData: WebIdVerificationAttempt = {
      firstName: firstName,
      lastName: lastName,
      dateOfBirth: toIsoBirthdate(normalizeBirthdate(birthdate)),
      ...(userId ? { email: userEmail?.trim() || '' } : {}),
      mobileNumber: phoneNumber.trim(),
      triggeredBy: clientId,
    };
    isProfileCheckInFlight.current = true;
    setIsRequestPending(true);

    try {
      trackEvent('is24.de.sicherheit.verifizierung.modal', 'verification_started', 'user', clientId, 'webid');

      const startVerificationResponse = await startWebIdVerification(webIdVerificationData, userId);
      if (!startVerificationResponse.sessionId) {
        onError('errors.webid.bad.request' as any);
        return;
      }
      if (startVerificationResponse.profileExist || startVerificationResponse.isprfiecheck) {
        setPhoneNumberForTan(phoneNumber.trim());
        navigateToWebIdFlow('verification_webid_tan', startVerificationResponse.sessionId);
        return;
      }
      navigateToWebIdFlow('webid_verification_start', startVerificationResponse.sessionId);
      trackEvent('is24.de.sicherheit.verifizierung.modal', 'webid_profile_not_found', 'user', clientId, 'webid');
    } catch (error) {
      if (error instanceof ApiResponseError) {
        if (error.responseStatus === 400) {
          onError('errors.webid.bad.request' as any);
        } else if (error.responseStatus === 429) {
          onError('errors.webid.request.timeout' as any);
        } else if (error.responseStatus === 401) {
          onError('errors.unauthorized');
        } else {
          onError('errors.default');
        }
      } else {
        onError('errors.default');
      }
    } finally {
      isProfileCheckInFlight.current = false;
      setIsRequestPending(false);
    }
  };

  const toIsoBirthdate = (normalizedBirthdate: string): string => {
    const match = normalizedBirthdate.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (!match) {
      return normalizedBirthdate;
    }
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  };

  return (
    <div className='modal-footer verification-modal-footer'>
      <div className='grid grid-flex'>
        <div className='grid-item grid-align-center one-half palm-one-whole'></div>
        <div className='grid-item one-half palm-one-whole'>
          <div className='grid-flex grid-justify-end verification-footer-div palm-one-whole'>
            <ButtonRounded
              data-testid='webid-verification-button'
              className='verification-footer-button'
              loading={isRequestPending}
              label={t('VerificationLandingPage.buttonText')}
              size='medium'
              onClick={onWebIdVerificationClick}
              disabled={isRequestPending}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebIdVerification;
